// Guardar lo que el usuario aceptó de una sugerencia de IA —
// ADR-2026-09-08-ficha-con-ia y ADR-2026-09-08-acciones-en-lote.
//
// Vive afuera de los componentes porque lo usan dos: la ficha de un producto y
// el panel en lote. Es el MISMO camino en los dos casos —el lote es esto mismo,
// N veces— y tenerlo dos veces sería tener dos formas de guardar lo mismo, que
// se van despegando.
import { resolverCamino, type CategoriaPlana } from './arbolDeCategorias';
import type { CampoIa, SugerenciaDeFicha } from './fichaIa';
import { actualizarFicha, marcarCategoriaDeProducto, type CamposDeFicha } from './lanzamientosApi';

export interface DepsDeAplicar {
  productoId: string;
  companyId: string;
  /** Qué campos aceptó el usuario. Lo demás de la sugerencia se ignora. */
  campos: readonly CampoIa[];
  sugerencia: SugerenciaDeFicha;
  /** Las categorías que el producto ya tiene: no se vuelven a marcar. */
  yaTiene: readonly string[];
  /** El árbol actual, para saber qué tramo de un camino nuevo ya existe. */
  categorias: readonly CategoriaPlana[];
  crearCategoria: (
    nombre: string,
    madre: string | null,
  ) => Promise<{ id: string | null; error: string | null }>;
  /**
   * El destino admite UNA categoría por producto (VTEX). Se queda la primera
   * que resuelva —existente antes que nueva— y REEMPLAZA la que tuviera: aceptar
   * la sugerencia es decir "va acá", no "también acá"
   * (ADR-2026-09-13-lanzamientos-para-vtex).
   */
  unaSolaCategoria?: boolean;
}

/**
 * Los campos de la ficha van en UN update; las categorías, por separado, porque
 * viven en otra tabla.
 *
 * Devuelve el primer error que aparezca, sin cortar lo demás: si falla crear una
 * categoría, la descripción y el precio ya guardados se quedan. Es preferible a
 * revertir todo y dejar al usuario sin nada de lo que aceptó.
 */
export async function aplicarSugerencia(d: DepsDeAplicar): Promise<{ error: string | null }> {
  const { campos, sugerencia: s } = d;
  let error: string | null = null;

  const patch: CamposDeFicha = {};
  if (campos.includes('descripcion') && s.descripcion) patch.descripcion = s.descripcion;
  if (campos.includes('marca') && s.marca) patch.marca = s.marca;
  if (campos.includes('tags') && s.tags) patch.tags = s.tags;
  if (campos.includes('seo')) {
    if (s.seoTitulo) patch.seoTitulo = s.seoTitulo;
    if (s.seoDescripcion) patch.seoDescripcion = s.seoDescripcion;
  }
  if (campos.includes('envio')) {
    if (s.peso) patch.peso = s.peso;
    if (s.alto) patch.alto = s.alto;
    if (s.ancho) patch.ancho = s.ancho;
    if (s.profundidad) patch.profundidad = s.profundidad;
  }
  if (Object.keys(patch).length > 0) await actualizarFicha(d.productoId, patch);

  if (!campos.includes('categorias')) return { error };

  const aPoner = [...(s.categoriaIds ?? [])];
  // Con una sola categoría, una existente que sirva gana y no hace falta crear.
  const soloUna = d.unaSolaCategoria === true;
  if (soloUna) aPoner.splice(1);

  // Las propuestas como nuevas se crean acá. `resolverCamino` dice cuánto del
  // camino ya existe: proponer "Remeras › Oversize" en una tienda que ya tiene
  // "Remeras" tiene que reusar esa, no crear una segunda.
  //
  // El árbol se actualiza en memoria a medida que se crea: en el lote, el
  // segundo producto que pide "Remeras" tiene que encontrarla ya creada por el
  // primero, y el refetch todavía no volvió.
  const arbol: CategoriaPlana[] = [...d.categorias];
  for (const camino of s.categoriasNuevas ?? []) {
    if (soloUna && aPoner.length > 0) break;
    const { madreId, faltan } = resolverCamino(arbol, camino);
    let padre = madreId;
    let ok = true;
    for (const nombre of faltan) {
      const r = await d.crearCategoria(nombre, padre);
      if (!r.id) {
        // Si un tramo falla se corta ese camino: colgar el resto de la raíz
        // dejaría una categoría suelta que el usuario no pidió.
        error = error ?? r.error;
        ok = false;
        break;
      }
      arbol.push({ id: r.id, nombre, parentId: padre });
      padre = r.id;
    }
    if (ok && padre) aPoner.push(padre);
  }

  if (soloUna && aPoner.length > 0 && !d.yaTiene.includes(aPoner[0]!)) {
    // Reemplaza: se sacan las que tenía ANTES de poner la nueva, así el producto
    // nunca queda con dos en un destino que admite una.
    for (const id of d.yaTiene) {
      const r = await marcarCategoriaDeProducto(d.productoId, id, d.companyId, false);
      error = error ?? r.error;
    }
  }
  for (const id of aPoner) {
    if (d.yaTiene.includes(id)) continue;
    const r = await marcarCategoriaDeProducto(d.productoId, id, d.companyId, true);
    error = error ?? r.error;
  }

  return { error };
}
